import React, { useState, useEffect } from 'react';
import { expensesAPI } from '../services/api';

const ExpenseModal = ({ isOpen, onClose, onSuccess, expense = null }) => {
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    financial_year: new Date().getFullYear(),
  });
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [keepFile, setKeepFile] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (expense) {
      // Format date properly for date input (YYYY-MM-DD)
      const formattedDate = expense.date ? expense.date.split('T')[0] : '';
      
      setFormData({
        date: formattedDate,
        description: expense.description,
        amount: expense.amount,
        financial_year: expense.financial_year || new Date().getFullYear(),
      });
      if (expense.file_name) {
        setExistingFile(expense.file_name);
        setKeepFile(true);
      }
    } else {
      setFormData({ 
        date: '', 
        description: '', 
        amount: '',
        financial_year: new Date().getFullYear(),
      });
      setFile(null);
      setExistingFile(null);
      setKeepFile(true);
    }
    setError('');
  }, [expense, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('date', formData.date);
      submitData.append('description', formData.description);
      submitData.append('amount', formData.amount);
      submitData.append('financial_year', formData.financial_year);
      
      if (file) {
        submitData.append('file', file);
      }
      
      if (expense) {
        submitData.append('keepFile', keepFile.toString());
      }

      if (expense) {
        await expensesAPI.update(expense.id, submitData);
      } else {
        await expensesAPI.create(submitData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setKeepFile(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{expense ? 'Edit Expense' : 'Add New Expense'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Financial Year *</label>
            <select
              value={formData.financial_year}
              onChange={(e) => setFormData({ ...formData, financial_year: e.target.value })}
              required
            >
              {Array.from({ length: 11 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>
                    {year} - {year + 1}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder="Enter expense description"
            />
          </div>

          <div className="form-group">
            <label>Amount ($) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label>Attach PDF File</label>
            
            {existingFile && keepFile && !file && (
              <div className="file-info">
                <div>Current file: {existingFile}</div>
                <div className="file-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={handleRemoveFile}
                  >
                    Remove File
                  </button>
                </div>
              </div>
            )}

            {(!existingFile || !keepFile || file) && (
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="file-input"
                  className="file-input"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
                <label htmlFor="file-input" className="file-label">
                  <span>📎</span>
                  <span>{file ? file.name : 'Choose PDF file'}</span>
                </label>
              </div>
            )}

            {file && (
              <div className="file-info">
                <div>Selected: {file.name}</div>
                <div className="file-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setFile(null);
                      if (existingFile) setKeepFile(true);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (expense ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseModal;